import React, { useEffect } from "react";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { CacheProvider } from "@emotion/react";
import "regenerator-runtime/runtime";

import { QueryClient, QueryClientProvider } from "react-query";

import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import LocalizationProvider from "@mui/lab/LocalizationProvider";

import "../vendor/jvectormap.css";
import "../vendor/perfect-scrollbar.css";
import "../vendor/apexcharts-mobile.css";

import "../i18n";
import createTheme from "../theme";

import { ThemeProvider } from "../contexts/ThemeContext";
import useTheme from "../hooks/useTheme";
import createEmotionCache from "../utils/createEmotionCache";

// import { AuthProvider } from "../contexts/JWTContext";
// import { AuthProvider } from "./contexts/FirebaseAuthContext";
import { AuthProvider } from "../contexts/Auth0Context";
// import { AuthProvider } from "./contexts/CognitoContext";
import { loadData } from "@aceso/utils/getClusterInfo";
import { RefreshProvider } from "@aceso/contexts/RefreshContext";
import { NotificationProvider } from "@aceso/contexts/NotificationContext";

const clientSideEmotionCache = createEmotionCache();

const queryClient = new QueryClient();

function App({ Component, emotionCache = clientSideEmotionCache, pageProps }) {
  const { theme } = useTheme();

  // useServiceWorker();
  useEffect(() => {
    const clientId = localStorage.getItem("auth0_healthcare_clientID");
    const orgId = localStorage.getItem("auth0_orgID");

    // Only load data if BOTH are missing and we haven't attempted to load yet
    if (!clientId || !orgId) {
      const hasAttemptedLoad = sessionStorage.getItem("auth0_load_attempted");

      if (!hasAttemptedLoad) {
        sessionStorage.setItem("auth0_load_attempted", "true");
        loadData();
      }
    }
  }, []);
  useEffect(() => {
    if (typeof window !== "undefined" && "Notification" in window) {
      // Kiosk wallboard screens have no user to answer the permission prompt,
      // so it would sit on top of the room grid forever.
      if (window.location.pathname.startsWith("/wallboard")) return;
      if (Notification.permission === "default") {
        Notification.requestPermission();
      }
    }
  }, []);
  // useEffect(() => {
  //   if (typeof window !== "undefined" && "serviceWorker" in navigator) {
  //     const messaging = getMessaging(firebaseApp);

  //     requestPermission(messaging);
  //   }
  // }, []);
  useEffect(() => {
    const link = document.createElement("link");
    link.href =
      "https://fonts.googleapis.com/css?family=Nunito&display=optional";
    link.rel = "stylesheet";
    document.head.appendChild(link);
    const link1 = document.createElement("link");
    link1.href = "https://fonts.googleapis.com/css?family=Poppins&display=swap";
    link1.rel = "stylesheet";
    document.head.appendChild(link1);
  }, []);

  const getLayout = Component.getLayout ?? ((page) => page);

  return (
    <CacheProvider value={emotionCache}>
      <QueryClientProvider client={queryClient}>
        <HelmetProvider>
          <Helmet titleTemplate="%s" defaultTitle="Healthcare portal" />
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <MuiThemeProvider theme={createTheme(theme)}>
              <AuthProvider>
                <RefreshProvider>
                  <NotificationProvider>
                    {getLayout(<Component {...pageProps} />)}
                  </NotificationProvider>
                </RefreshProvider>
              </AuthProvider>
            </MuiThemeProvider>
          </LocalizationProvider>
        </HelmetProvider>
      </QueryClientProvider>
    </CacheProvider>
  );
}

const withThemeProvider = (Component) => {
  const AppWithThemeProvider = (props) => {
    return (
      <ThemeProvider>
        <Component {...props} />
      </ThemeProvider>
    );
  };
  AppWithThemeProvider.displayName = "AppWithThemeProvider";
  return AppWithThemeProvider;
};

export default withThemeProvider(App);
